<template>
  <div
    class="deal-workflow-node"
    :class="[
      `deal-workflow-node--${kindVariant(item.kind)}`,
      { 'deal-workflow-node--child': isChild },
    ]"
    :style="depthStyle"
  >
    <div class="d-flex flex-wrap align-start">
      <span
        class="deal-workflow-node__icon mr-3"
        :class="`deal-workflow-node__icon--${kindVariant(item.kind)}`"
      >
        <v-icon small>{{ kindIcon(item.kind) }}</v-icon>
      </span>

      <div class="deal-workflow-node__content flex-grow-1 min-width-0">
        <div class="d-flex flex-wrap align-center">
          <v-chip
            x-small
            class="mr-2 deal-workflow-node__kind-chip"
            :class="`deal-workflow-node__kind-chip--${kindVariant(item.kind)}`"
          >
            {{ kindLabel(item.kind) }}
          </v-chip>
          <router-link :to="nodeRoute(item)" class="font-weight-medium mr-2">
            {{ item.code }}
          </router-link>
          <v-chip x-small>{{ item.status_label }}</v-chip>

          <template v-if="showProgress">
            <v-progress-linear
              :value="item.progress.percent"
              height="6"
              rounded
              class="deal-workflow-node__progress ml-2"
              color="primary"
            />
            <span class="text-caption grey--text ml-2">{{ item.progress.percent }}%</span>
          </template>

          <v-spacer v-if="showActions" />

          <slot v-if="showActions" name="actions" />
        </div>

        <div class="text-caption grey--text mt-1">{{ item.summary }}</div>

        <div
          v-if="item.references && item.references.length"
          class="text-caption mt-1"
        >
          {{ $t("DEALS.VIEW.LINKED_TO") }}:
          <template v-for="(ref, refIndex) in item.references">
            <span :key="`${ref.kind}-${ref.id}`" class="deal-workflow-node__ref">
              <v-icon
                x-small
                class="deal-workflow-node__ref-icon"
                :class="`deal-workflow-node__ref-icon--${kindVariant(ref.kind)}`"
              >
                {{ kindIcon(ref.kind) }}
              </v-icon>
              <router-link :to="nodeRoute(ref)" class="mr-1">
                {{ ref.code }}
              </router-link>
            </span>
            <span v-if="refIndex < item.references.length - 1" :key="`sep-${refIndex}`">,</span>
          </template>
        </div>

        <DealWorkflowFiles
          v-if="item.file_count"
          class="mt-2"
          :files="item.files"
          :downloading-file-id="downloadingFileId"
          @download="$emit('download-file', $event)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import DealWorkflowFiles from "@/views/pages/deals/partials/DealWorkflowFiles.vue";
import {
  kindIcon,
  kindLabel,
  kindVariant,
  nodeRoute,
} from "@/views/pages/deals/dealWorkflowMeta";

export default {
  name: "DealWorkflowNode",
  components: { DealWorkflowFiles },
  props: {
    item: {
      type: Object,
      required: true,
    },
    isChild: {
      type: Boolean,
      default: false,
    },
    showActions: {
      type: Boolean,
      default: false,
    },
    showProgress: {
      type: Boolean,
      default: false,
    },
    downloadingFileId: {
      type: [Number, String],
      default: null,
    },
    depth: {
      type: Number,
      default: 0,
    },
  },
  computed: {
    depthStyle() {
      if (!this.isChild || this.depth <= 0) {
        return null;
      }
      return {
        marginLeft: `${18 * this.depth}px`,
      };
    },
  },
  methods: {
    kindIcon,
    kindLabel,
    kindVariant,
    nodeRoute,
  },
};
</script>

<style scoped>
.deal-workflow-node {
  padding: 12px;
  border-radius: 8px;
  background: #fff;
}

.deal-workflow-node--child {
  border-left: 3px solid #e3e6ef;
  padding-left: 16px;
}

.deal-workflow-node--child.deal-workflow-node--sales-order {
  border-left-color: #00897b;
}

.deal-workflow-node--child.deal-workflow-node--purchase-order {
  border-left-color: #3949ab;
}

.deal-workflow-node--child.deal-workflow-node--receipt {
  border-left-color: #f57c00;
}

.deal-workflow-node--child.deal-workflow-node--delivery {
  border-left-color: #7b1fa2;
}

.deal-workflow-node--child.deal-workflow-node--transport {
  border-left-color: #0288d1;
}

.deal-workflow-node__content {
  min-width: 0;
}

.deal-workflow-node__progress {
  max-width: 120px;
  min-width: 80px;
}

.deal-workflow-node__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 10px;
}

.deal-workflow-node__icon--purchase-order {
  background: rgba(57, 73, 171, 0.1);
  color: #3949ab;
}

.deal-workflow-node__icon--sales-order {
  background: rgba(0, 137, 123, 0.1);
  color: #00897b;
}

.deal-workflow-node__icon--receipt {
  background: rgba(245, 124, 0, 0.1);
  color: #f57c00;
}

.deal-workflow-node__icon--delivery {
  background: rgba(123, 31, 162, 0.1);
  color: #7b1fa2;
}

.deal-workflow-node__icon--transport {
  background: rgba(2, 136, 209, 0.1);
  color: #0288d1;
}

.deal-workflow-node__icon .v-icon {
  color: inherit !important;
}

.deal-workflow-node__kind-chip {
  font-weight: 600;
}

.deal-workflow-node__kind-chip--purchase-order {
  background: rgba(57, 73, 171, 0.12) !important;
  color: #3949ab !important;
}

.deal-workflow-node__kind-chip--sales-order {
  background: rgba(0, 137, 123, 0.12) !important;
  color: #00897b !important;
}

.deal-workflow-node__kind-chip--receipt {
  background: rgba(245, 124, 0, 0.12) !important;
  color: #f57c00 !important;
}

.deal-workflow-node__kind-chip--delivery {
  background: rgba(123, 31, 162, 0.12) !important;
  color: #7b1fa2 !important;
}

.deal-workflow-node__kind-chip--transport {
  background: rgba(2, 136, 209, 0.12) !important;
  color: #0288d1 !important;
}

.deal-workflow-node__ref {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
}

.deal-workflow-node__ref-icon {
  margin-right: 2px;
}

.deal-workflow-node__ref-icon--purchase-order {
  color: #3949ab !important;
}

.deal-workflow-node__ref-icon--sales-order {
  color: #00897b !important;
}

.deal-workflow-node__ref-icon--receipt {
  color: #f57c00 !important;
}

.deal-workflow-node__ref-icon--delivery {
  color: #7b1fa2 !important;
}

.deal-workflow-node__ref-icon--transport {
  color: #0288d1 !important;
}
</style>
